import React, { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { findLine, wholeNumber } from './quickFind';
import { isUncounted, qtyBoxValue } from './lineState';

/*
  THE QUICK ENTRY BAR, rebrand step 5, 17 September 2026 (brand guide 12.3).

  On a computer, above the grid: type or scan an item number, press Enter, type
  the count, press Enter. A barcode scanner types into the first box and presses
  Enter itself, so it needs nothing set up.

  - **It finds lines already on this sheet and nothing else** (`quickFind.js`).
    It asks the database nothing. An item that is not on the sheet is added with
    the "Add an item" search below it, as before.
  - **The count goes through the one save**: `onSave` is count entry's
    `updateQty`, the same function every count box calls, with every rule it
    carries (who holds the sheet, the regional office exception, the history
    row, a number held with no signal).
  - "Saved [item]: [count]" only appears when the save says it landed.
*/
const RESET_MS = 450;

const BarcodeIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
    <path d="M3 7V5a2 2 0 0 1 2-2h2M17 3h2a2 2 0 0 1 2 2v2M21 17v2a2 2 0 0 1-2 2h-2M7 21H5a2 2 0 0 1-2-2v-2" />
    <path d="M7 8v8M10 8v8M13 8v8M16 8v8" />
  </svg>
);

const QuickEntry = forwardRef(function QuickEntry({ items, onFind, onSave }, ref) {
  const itemRef = useRef(null);
  const countRef = useRef(null);
  const [text, setText] = useState('');
  const [count, setCount] = useState('');
  const [line, setLine] = useState(null);
  const [problem, setProblem] = useState(null);   // { field, says }
  const [done, setDone] = useState(null);         // "Saved Knee brace: 12"
  const [busy, setBusy] = useState(false);
  const resetTimer = useRef(null);

  useImperativeHandle(ref, () => ({
    focus: () => { itemRef.current?.focus(); itemRef.current?.select(); },
  }));

  // The line as it is now, so its count updates after a save or a change elsewhere.
  const current = line ? (items.find(i => i.id === line.id) || null) : null;

  function refuse(field, says) {
    // Clear first so the jostle plays again on a second refusal.
    setProblem(null);
    requestAnimationFrame(() => setProblem({ field, says }));
  }

  function find(e) {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    const found = findLine(items, text);
    setDone(null);
    if (!found) {
      setLine(null);
      refuse('item', 'No item matches ' + text.trim() + '. Try the item number from the label.');
      return;
    }
    setProblem(null);
    setLine(found);
    setCount('');
    onFind(found);
    // The Count box is switched on by this same change, so it takes the cursor
    // once it has been drawn.
    setFocusCount(n => n + 1);
  }
  const [focusCount, setFocusCount] = useState(0);
  React.useEffect(() => {
    if (focusCount) countRef.current?.focus();
  }, [focusCount]);

  async function save() {
    if (!current || busy) return;
    const n = wholeNumber(count);
    if (n === null) { refuse('count', 'Enter a number, like 12.'); return; }
    setProblem(null);
    setBusy(true);
    const outcome = await onSave(current, n);
    setBusy(false);
    if (outcome !== 'saved' && outcome !== 'same') return;
    setDone('Saved ' + (current.description_raw || current.item_number_raw) + ': ' + n);
    clearTimeout(resetTimer.current);
    resetTimer.current = setTimeout(() => {
      setText(''); setCount(''); setLine(null);
      itemRef.current?.focus();
    }, RESET_MS);
  }

  function countKey(e) {
    if (e.key === 'Enter') { e.preventDefault(); save(); return; }
    if (e.key === 'Escape') {
      e.preventDefault();
      setCount(''); setProblem(null);
      itemRef.current?.focus(); itemRef.current?.select();
    }
  }

  React.useEffect(() => () => clearTimeout(resetTimer.current), []);

  return (
    <div className="qe" role="search" aria-label="Quick entry">
      <div className="qe-row">
        <label className="qe-item">
          <span className="qe-label">Scan or type item number</span>
          <span className="qe-field">
            <BarcodeIcon />
            <input ref={itemRef} className="input" value={text} autoComplete="off" spellCheck={false}
              aria-invalid={problem?.field === 'item' ? 'true' : undefined}
              aria-describedby="qe-says"
              onChange={e => { setText(e.target.value); if (problem?.field === 'item') setProblem(null); }}
              onKeyDown={find} placeholder="Item number or name" />
          </span>
        </label>
        <label className="qe-count">
          <span className="qe-label">Count</span>
          <input ref={countRef} className="input" inputMode="numeric" value={count} autoComplete="off"
            disabled={!current}
            aria-invalid={problem?.field === 'count' ? 'true' : undefined}
            aria-describedby="qe-says"
            onChange={e => { setCount(e.target.value); if (problem?.field === 'count') setProblem(null); }}
            onKeyDown={countKey} />
        </label>
        <button type="button" className="btn btn-primary btn-lg qe-save" disabled={!current || busy} onClick={save}>
          Save
        </button>
      </div>
      <div id="qe-says" className="qe-says" aria-live="polite">
        {problem ? (
          <span className="qe-problem">{problem.says}</span>
        ) : done ? (
          <span className="qe-done">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12.5l4.5 4.5L19 7.5" /></svg>
            {done}
          </span>
        ) : current ? (
          <span className="qe-found">
            <b>{current.description_raw}</b>
            {current.location ? <span>Shelf {current.location}</span> : null}
            <span>{current.item_number_raw}</span>
            <span>Now {isUncounted(current) ? 'not counted' : qtyBoxValue(current)}</span>
          </span>
        ) : (
          /* The keyboard hints (guide 12.3: "a keyboard hint line near the
             title"). They were a row of their own along the bottom of the list
             before rebrand step 5; the four keys it named are here with three
             more. */
          <span className="qe-hint kbd-line">
            <span className="kbd-hint"><kbd>Enter</kbd> save and next</span>
            <span className="kbd-hint"><kbd>Up</kbd> <kbd>Down</kbd> move</span>
            <span className="kbd-hint"><kbd>Esc</kbd> undo</span>
            <span className="kbd-hint"><kbd>/</kbd> quick entry</span>
            <span className="kbd-hint"><kbd>N</kbd> note on this line</span>
            <span className="kbd-say">Every change saves on its own. Save Progress is there for peace of mind.</span>
          </span>
        )}
      </div>
    </div>
  );
});

export default QuickEntry;
